import { Info, Leaf, Map, Users } from "lucide-react"
import type { ComponentType } from "react"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useCooperativeKpis } from "@/features/cooperatives/hooks/use-cooperatives"
import { formatNumber } from "@/lib/format"
import { cn } from "@/lib/utils"

interface CooperativeKpisProps {
  groupId: string | number | undefined
}

interface KpiTileProps {
  label: string
  tooltip: string
  icon: ComponentType<{ className?: string }>
  value: string
  hint: string
  loading?: boolean
  tone: "info" | "success" | "muted"
}

const tones = {
  info: {
    bg: "bg-sky-100 dark:bg-sky-500/15",
    fg: "text-sky-600 dark:text-sky-300",
  },
  success: {
    bg: "bg-emerald-100 dark:bg-emerald-500/15",
    fg: "text-emerald-600 dark:text-emerald-300",
  },
  muted: { bg: "bg-muted", fg: "text-muted-foreground" },
}

function KpiTile({
  label,
  tooltip,
  icon: Icon,
  value,
  hint,
  loading,
  tone,
}: KpiTileProps) {
  const palette = tones[tone]
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              {label}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={label}
                  className="inline-flex text-muted-foreground hover:text-foreground"
                >
                  <Info className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
            </Tooltip>
          </div>
          {loading ? (
            <>
              <Skeleton className="h-7 w-20" />
              <Skeleton className="h-3.5 w-28" />
            </>
          ) : (
            <>
              <p className="text-2xl font-semibold tabular-nums">{value}</p>
              <p className="text-xs text-muted-foreground">{hint}</p>
            </>
          )}
        </div>
        <div className={cn("rounded-lg p-2", palette.bg)}>
          <Icon className={cn("size-5", palette.fg)} />
        </div>
      </CardContent>
    </Card>
  )
}

export function CooperativeKpis({ groupId }: CooperativeKpisProps) {
  const { t } = useTranslation(["cooperatives", "common"])
  const { data, isLoading, isError } = useCooperativeKpis(groupId)

  if (isError) return null

  const producers = data?.producers
  const traceability = data?.traceability
  const parcels = data?.parcelCompliance

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <KpiTile
        label={t("kpis.producers")}
        tooltip={t("kpis.producersTooltip")}
        icon={Users}
        loading={isLoading || !producers}
        value={producers ? formatNumber(producers.totalProducers) : ""}
        hint={
          producers
            ? t("kpis.producersHint", {
                hectares: formatNumber(producers.totalHectares, 2),
              })
            : ""
        }
        tone="info"
      />
      <KpiTile
        label={t("kpis.traceableProducers")}
        tooltip={t("kpis.traceableProducersTooltip")}
        icon={Leaf}
        loading={isLoading || !traceability}
        value={
          traceability ? `${formatNumber(traceability.percentage, 0)} %` : ""
        }
        hint={
          traceability
            ? t("kpis.traceableProducersHint", {
                compliant: formatNumber(traceability.compliantProducers),
                total: formatNumber(traceability.totalProducers),
              })
            : ""
        }
        tone="success"
      />
      <KpiTile
        label={t("kpis.compliantParcels")}
        tooltip={t("kpis.compliantParcelsTooltip")}
        icon={Map}
        loading={isLoading || !parcels}
        value={parcels ? `${formatNumber(parcels.percentage, 0)} %` : ""}
        hint={
          parcels
            ? t("kpis.compliantParcelsHint", {
                compliant: formatNumber(parcels.compliantParcels),
                total: formatNumber(parcels.totalParcels),
              })
            : ""
        }
        tone="muted"
      />
    </div>
  )
}
